<template>
  <div class="kongqiulv-container">
    <div class="header">
      <h1 class="title">控球率统计</h1>
    </div>

    <div class="content">
      <!-- 比赛倒计时 -->
      <div class="match-timer-card" v-if="matchStarted">
        <div class="timer-label">比赛时间</div>
        <div class="match-timer">{{ formatTime(matchTime) }}</div>
        <div class="timer-status">{{ matchTime <= 0 ? '比赛结束' : '比赛中' }}</div>
        <div v-if="injuryTime > 0" class="injury-time-display">补时: +{{ injuryTime }}分钟</div>
      </div>

      <!-- 开始比赛按钮 -->
      <div v-if="!matchStarted" class="start-section">
        <button class="btn-start" @click="startMatch">开始比赛</button>
      </div>

      <!-- 计时器控制区域 -->
      <div v-if="matchStarted" class="timers-section">
        <!-- 主队进攻时间 -->
        <div class="timer-card" :class="{ active: currentTimer === 'home' }">
          <div class="timer-header">
            <div class="timer-title">主队进攻</div>
            <button 
              class="btn-timer" 
              @click="switchTimer('home')"
              :disabled="matchTime <= 0"
            >
              {{ currentTimer === 'home' ? '进行中' : '切换' }}
            </button>
          </div>
          <div class="timer-value">{{ formatTime(homeAttackTime) }}</div>
          <div class="timer-percent">控球率: {{ homePossession }}%</div>
        </div>

        <!-- 客队进攻时间 -->
        <div class="timer-card" :class="{ active: currentTimer === 'away' }">
          <div class="timer-header">
            <div class="timer-title">客队进攻</div>
            <button 
              class="btn-timer" 
              @click="switchTimer('away')"
              :disabled="matchTime <= 0"
            >
              {{ currentTimer === 'away' ? '进行中' : '切换' }}
            </button>
          </div>
          <div class="timer-value">{{ formatTime(awayAttackTime) }}</div>
          <div class="timer-percent">控球率: {{ awayPossession }}%</div>
        </div>

        <!-- 死球时间 -->
        <div class="timer-card" :class="{ active: currentTimer === 'dead' }">
          <div class="timer-header">
            <div class="timer-title">死球时间</div>
            <button 
              class="btn-timer" 
              @click="switchTimer('dead')"
              :disabled="matchTime <= 0"
            >
              {{ currentTimer === 'dead' ? '进行中' : '切换' }}
            </button>
          </div>
          <div class="timer-value">{{ formatTime(deadBallTime) }}</div>
        </div>
      </div>

      <!-- 补时设置（比赛进行中） -->
      <div v-if="matchStarted" class="injury-time-section">
        <div class="injury-time-setting">
          <label class="injury-time-label">添加补时（分钟）：</label>
          <div class="injury-time-controls">
            <button 
              class="btn btn-minus-small" 
              @click="decreaseInjuryTime"
              :disabled="injuryTime <= 0"
            >
              <span class="btn-icon">−</span>
            </button>
            <div class="injury-time-value">{{ injuryTime }}</div>
            <button 
              class="btn btn-plus-small" 
              @click="increaseInjuryTime"
            >
              <span class="btn-icon">+</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div v-if="matchStarted" class="action-section">
        <button class="btn-action btn-pause" @click="togglePause">
          {{ isPaused ? '继续' : '暂停' }}
        </button>
        <button class="btn-action btn-reset" @click="resetMatch">重置</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'KongqiulvView',
  setup() {
    const route = useRoute()
    const matchStarted = ref(false)
    const isPaused = ref(false)
    const injuryTime = ref(0) // 补时时间（分钟）
    const matchTime = ref(90 * 60) // 比赛时间，单位：秒（初始为90分钟，开始时会加上补时）
    const homeAttackTime = ref(0)
    const awayAttackTime = ref(0)
    const deadBallTime = ref(0)
    const currentTimer = ref('dead') // 'home', 'away', 'dead'
    
    // 存储 team_id 和 match_id
    const homeTeamId = ref(null)
    const awayTeamId = ref(null)
    const matchId = ref(null)
    
    let matchInterval = null
    let timerInterval = null

    // 格式化时间显示（秒转 mm:ss）
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }

    // 计算控球率
    const homePossession = computed(() => {
      const total = homeAttackTime.value + awayAttackTime.value
      if (total === 0) return 0
      return Math.round((homeAttackTime.value / total) * 100)
    })

    const awayPossession = computed(() => {
      const total = homeAttackTime.value + awayAttackTime.value
      if (total === 0) return 0
      return Math.round((awayAttackTime.value / total) * 100)
    })

    // 增加补时（比赛进行中）
    const increaseInjuryTime = () => {
      if (matchStarted.value) {
        // 增加补时时间
        injuryTime.value++
        // 同时增加比赛剩余时间（补时1分钟 = 60秒）
        matchTime.value += 60
        saveState()
      }
    }

    // 减少补时（比赛进行中）
    const decreaseInjuryTime = () => {
      if (matchStarted.value && injuryTime.value > 0) {
        // 减少补时时间
        injuryTime.value--
        // 同时减少比赛剩余时间（补时1分钟 = 60秒）
        // 但不能让比赛时间变成负数
        matchTime.value = Math.max(0, matchTime.value - 60)
        saveState()
      }
    }

    // 开始比赛
    const startMatch = () => {
      // 比赛开始时，补时为0，总时间为90分钟
      injuryTime.value = 0
      matchTime.value = 90 * 60
      matchStarted.value = true
      isPaused.value = false
      currentTimer.value = 'dead'
      startTimers()
      saveState()
    }

    // 切换计时器
    const switchTimer = (type) => {
      if (isPaused.value || matchTime.value <= 0) return
      currentTimer.value = type
      saveState()
    }

    // 暂停/继续
    const togglePause = () => {
      isPaused.value = !isPaused.value
      if (isPaused.value) {
        stopTimers()
      } else {
        startTimers()
      }
      saveState()
    }

    // 开始计时器
    const startTimers = () => {
      // 比赛倒计时
      matchInterval = setInterval(() => {
        if (!isPaused.value && matchTime.value > 0) {
          matchTime.value--
          if (matchTime.value <= 0) {
            stopTimers()
          }
          saveState()
        }
      }, 1000)

      // 当前选中的计时器
      timerInterval = setInterval(() => {
        if (!isPaused.value && matchTime.value > 0) {
          if (currentTimer.value === 'home') {
            homeAttackTime.value++
          } else if (currentTimer.value === 'away') {
            awayAttackTime.value++
          } else if (currentTimer.value === 'dead') {
            deadBallTime.value++
          }
          saveState()
        }
      }, 1000)
    }

    // 停止计时器
    const stopTimers = () => {
      if (matchInterval) {
        clearInterval(matchInterval)
        matchInterval = null
      }
      if (timerInterval) {
        clearInterval(timerInterval)
        timerInterval = null
      }
    }

    // 重置比赛
    const resetMatch = () => {
      if (confirm('确定要重置所有数据吗？')) {
        stopTimers()
        matchStarted.value = false
        isPaused.value = false
        injuryTime.value = 0
        matchTime.value = 90 * 60
        homeAttackTime.value = 0
        awayAttackTime.value = 0
        deadBallTime.value = 0
        currentTimer.value = 'dead'
        saveState()
      }
    }

    // 获取统计数据
    const fetchStatistics = async () => {
      const currentMatchId = route.query.match_id || route.params.match_id
      if (!currentMatchId) {
        console.warn('未找到 match_id，跳过获取统计数据')
        return
      }
      
      matchId.value = currentMatchId
      
      try {
        const response = await fetch(`https://api.zctj16888.com/app/v1/other/match_statistics?match_id=${currentMatchId}`)
        const result = await response.json()
        
        if (result.code === 1 && result.data) {
          // 填充主队数据 (team_A)
          if (result.data.team_A) {
            homeTeamId.value = result.data.team_A.team_id
            // 控球率是百分比，需要转换为秒数（这里假设比赛总时间是90分钟）
            // 如果API返回的是百分比，我们需要根据总时间计算
            const possession = result.data.team_A.possession || 0
            // 如果返回的是百分比，计算对应的秒数
            if (possession > 0 && possession <= 100) {
              // 使用当前比赛总时间（90分钟 + 已添加的补时）
              // 注意：这里使用90分钟作为基准，因为API返回的控球率是基于90分钟计算的
              const totalActiveTime = 90 * 60
              homeAttackTime.value = Math.round((possession / 100) * totalActiveTime)
              awayAttackTime.value = totalActiveTime - homeAttackTime.value
            }
          }
          
          // 填充客队数据 (team_B)
          if (result.data.team_B) {
            awayTeamId.value = result.data.team_B.team_id
            // 如果客队有控球率数据，也更新
            const possession = result.data.team_B.possession || 0
            if (possession > 0 && possession <= 100) {
              // 使用当前比赛总时间（90分钟 + 已添加的补时）
              // 注意：这里使用90分钟作为基准，因为API返回的控球率是基于90分钟计算的
              const totalActiveTime = 90 * 60
              awayAttackTime.value = Math.round((possession / 100) * totalActiveTime)
              homeAttackTime.value = totalActiveTime - awayAttackTime.value
            }
          }
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    }

    // 上传统计数据
    const uploadStatistics = async (teamId, possession) => {
      if (!matchId.value || !teamId) {
        console.warn('缺少 match_id 或 team_id，无法上传')
        return
      }
      
      try {
        const response = await fetch('https://api.zctj16888.com/app/v1/other/statistics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            match_id: matchId.value,
            team_id: teamId,
            possession: possession
          })
        })
        
        const result = await response.json()
        if (result.code !== 1) {
          console.error('上传统计数据失败:', result.msg)
        }
      } catch (error) {
        console.error('上传统计数据失败:', error)
      }
    }

    // 保存状态到本地存储并上传
    const saveState = async () => {
      try {
        const state = {
          matchStarted: matchStarted.value,
          isPaused: isPaused.value,
          injuryTime: injuryTime.value,
          matchTime: matchTime.value,
          homeAttackTime: homeAttackTime.value,
          awayAttackTime: awayAttackTime.value,
          deadBallTime: deadBallTime.value,
          currentTimer: currentTimer.value
        }
        localStorage.setItem('kongqiulv_state', JSON.stringify(state))
        
        // 计算并上传控球率
        const totalActiveTime = homeAttackTime.value + awayAttackTime.value
        if (totalActiveTime > 0) {
          const homePossessionPercent = Math.round((homeAttackTime.value / totalActiveTime) * 100)
          const awayPossessionPercent = Math.round((awayAttackTime.value / totalActiveTime) * 100)
          
          // 上传主队控球率
          if (homeTeamId.value) {
            await uploadStatistics(homeTeamId.value, homePossessionPercent)
          }
          
          // 上传客队控球率
          if (awayTeamId.value) {
            await uploadStatistics(awayTeamId.value, awayPossessionPercent)
          }
        }
      } catch (error) {
        console.error('保存状态失败:', error)
      }
    }

    // 加载状态
    const loadState = () => {
      try {
        const saved = localStorage.getItem('kongqiulv_state')
        if (saved) {
          const state = JSON.parse(saved)
          matchStarted.value = state.matchStarted || false
          isPaused.value = state.isPaused || false
          injuryTime.value = state.injuryTime || 0
          matchTime.value = state.matchTime || 90 * 60
          homeAttackTime.value = state.homeAttackTime || 0
          awayAttackTime.value = state.awayAttackTime || 0
          deadBallTime.value = state.deadBallTime || 0
          currentTimer.value = state.currentTimer || 'dead'
          
          if (matchStarted.value && !isPaused.value && matchTime.value > 0) {
            startTimers()
          }
        }
      } catch (error) {
        console.error('加载状态失败:', error)
      }
    }

    onMounted(async () => {
      // 先获取服务器数据
      await fetchStatistics()
      // 然后加载本地状态（可能会覆盖服务器数据，但保留用户的操作状态）
      loadState()
    })

    onUnmounted(() => {
      stopTimers()
    })

    return {
      matchStarted,
      isPaused,
      injuryTime,
      matchTime,
      homeAttackTime,
      awayAttackTime,
      deadBallTime,
      currentTimer,
      homePossession,
      awayPossession,
      formatTime,
      increaseInjuryTime,
      decreaseInjuryTime,
      startMatch,
      switchTimer,
      togglePause,
      resetMatch
    }
  }
}
</script>

<style scoped>
.kongqiulv-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.header {
  width: 100%;
  max-width: 500px;
  margin-bottom: 20px;
}

.title {
  font-size: 24px;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  margin: 0;
}

.content {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.start-section {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.btn-start {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 15px 50px;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.btn-start:active {
  transform: scale(0.95);
}

.btn-start:hover {
  box-shadow: 0 6px 20px rgba(78, 205, 196, 0.5);
}

.match-timer-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.timer-label {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.match-timer {
  font-size: 48px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.timer-status {
  font-size: 14px;
  color: #999;
}

.timers-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.timer-card {
  background: white;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.timer-card.active {
  background: linear-gradient(135deg, #fff5e6 0%, #ffe6cc 100%);
  border: 2px solid #ff9800;
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.3);
}

.timer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.timer-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.btn-timer {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
  border: none;
  border-radius: 15px;
  padding: 6px 15px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-timer:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-timer:not(:disabled):active {
  transform: scale(0.95);
}

.timer-card.active .btn-timer {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.timer-value {
  font-size: 36px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin: 10px 0;
}

.timer-percent {
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-top: 5px;
}

.injury-time-section {
  width: 100%;
  margin-bottom: 10px;
}

.injury-time-setting {
  background: white;
  border-radius: 15px;
  padding: 15px 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.injury-time-label {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.injury-time-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.btn-minus-small,
.btn-plus-small {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-minus-small {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
}

.btn-minus-small:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-minus-small:not(:disabled):hover {
  background: linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%);
  box-shadow: 0 4px 12px rgba(238, 90, 111, 0.4);
}

.btn-plus-small {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
}

.btn-plus-small:hover {
  background: linear-gradient(135deg, #44a08d 0%, #4ecdc4 100%);
  box-shadow: 0 4px 12px rgba(78, 205, 196, 0.4);
}

.injury-time-value {
  font-size: 32px;
  font-weight: bold;
  color: #333;
  min-width: 50px;
  text-align: center;
}

.injury-time-display {
  font-size: 14px;
  color: #ff9800;
  font-weight: 600;
  margin-top: 5px;
}

.action-section {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
}

.btn-action {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 20px;
  padding: 10px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-action:active {
  transform: scale(0.95);
}

.btn-action:hover {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn-pause {
  background: rgba(255, 152, 0, 0.3);
  border-color: rgba(255, 152, 0, 0.8);
}

.btn-pause:hover {
  background: rgba(255, 152, 0, 0.4);
}

/* 移动端适配 */
@media (max-width: 480px) {
  .kongqiulv-container {
    padding: 10px;
  }

  .title {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .match-timer-card {
    padding: 15px;
  }

  .match-timer {
    font-size: 40px;
  }

  .timer-card {
    padding: 12px;
  }

  .timer-title {
    font-size: 16px;
  }

  .timer-value {
    font-size: 32px;
  }

  .btn-timer {
    padding: 5px 12px;
    font-size: 12px;
  }

  .btn-start {
    padding: 12px 40px;
    font-size: 18px;
  }

  .btn-action {
    padding: 8px 24px;
    font-size: 14px;
  }

  .injury-time-setting {
    padding: 12px 15px;
  }

  .injury-time-label {
    font-size: 14px;
  }

  .injury-time-value {
    font-size: 28px;
  }

  .btn-minus-small,
  .btn-plus-small {
    width: 35px;
    height: 35px;
    font-size: 20px;
  }

  .total-time-info {
    font-size: 14px;
  }
}
</style>

